import logger from '../utils/logger';
/**
 * Unified API Configuration for Web Application
 * Single source of truth for all backend endpoints
 */

const ENV = import.meta.env.MODE || 'development';

// Environment utility functions (defined early to avoid TDZ errors)
export const getEnvironment = () => ENV;
export const isProduction = () => ENV === 'production';
export const isDevelopment = () => ENV === 'development';

// ==================== DEVELOPMENT TOGGLE ====================
// Use VH_API_TARGET environment variable to switch between local and production
//   VH_API_TARGET=local       -> Uses http://localhost:5001 (Local vh-api)
//   VH_API_TARGET=production  -> Uses production Cloud Run API
//
// Set via terminal: export VH_API_TARGET=local
// Or inline: VH_API_TARGET=local pnpm dev
//
// FALLBACK: If VH_API_TARGET is not set, uses VITE_API_URL from .env
// Note: Vite only exposes VITE_ prefixed vars, so check VITE_VH_API_TARGET first
const VH_API_TARGET = import.meta.env.VITE_VH_API_TARGET || import.meta.env.VH_API_TARGET || import.meta.env.VITE_API_TARGET;
const USE_LOCAL_API = VH_API_TARGET === 'local';
const USE_PRODUCTION_API = VH_API_TARGET === 'production';

// Export for debugging
export const getApiTarget = () => {
  if (USE_LOCAL_API) return 'local';
  if (USE_PRODUCTION_API) return 'production';
  return 'env-default';
};
// ============================================================

const normalizeUrl = (url) => {
  if (!url) return '';
  let normalized = url.replace(/\/$/, '');
  if (normalized.endsWith('/api')) {
    normalized = normalized.slice(0, -4);
  }
  return normalized;
};

// API URL priority: VH_API_TARGET > VITE_API_URL > environment default
const ENV_API_URL = (() => {
  // Priority 1: VH_API_TARGET override
  if (USE_LOCAL_API) return 'http://localhost:5001';
  if (USE_PRODUCTION_API) return 'https://virtual-hardhat-backend-a2dkmiwh5q-uc.a.run.app';

  // Priority 2: VITE_API_URL from .env file
  return normalizeUrl(
    import.meta.env.VITE_API_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      import.meta.env.VITE_BACKEND_URL
  );
})();

// Production guard: Disable localhost/HTTP ingest in production
const shouldUseIngest = () => {
  if (isProduction()) return false;
  if (typeof window === 'undefined') return false;
  // Only allow in development with explicit flag
  return import.meta.env.VITE_ENABLE_INGEST === 'true' && isDevelopment();
};

// Environment-specific base URLs
const API_URLS = {
  development: ENV_API_URL || 'http://localhost:5001',
  staging: ENV_API_URL || 'https://api.staging.virtual-hardhat.com',
  // Production: Use actual backend URL, not placeholder domain
  production: ENV_API_URL || 'https://virtual-hardhat-backend-a2dkmiwh5q-uc.a.run.app',
};

// Fallback for Azure deployment
const AZURE_URL = 'https://virtual-hardhat-backend.azurewebsites.net';

// Select appropriate URL based on environment
export const API_BASE_URL = normalizeUrl(API_URLS[ENV] || API_URLS.development);

// Production guard: Validate API URL in production
if (isProduction() && typeof window !== 'undefined') {
  if (!API_BASE_URL || API_BASE_URL.includes('localhost') || API_BASE_URL.includes('127.0.0.1') || API_BASE_URL.startsWith('http://')) {
    console.error('[API Config] Production API URL validation failed:', {
      apiBaseUrl: API_BASE_URL,
      env: ENV,
      viteApiUrl: import.meta.env.VITE_API_URL,
    });
  }
}

// API endpoints configuration
export const API_CONFIG = {
  // Base URLs
  baseURL: API_BASE_URL,
  apiURL: `${API_BASE_URL}/api`,
  timeout: 30000,

  // Feature flags
  enableOfflineMode: true,
  // VITE_ENABLE_REALTIME=true overrides dev default (off) for local socket testing
  enableRealTimeUpdates:
    import.meta.env.VITE_ENABLE_REALTIME === 'true'
      ? true
      : import.meta.env.VITE_ENABLE_REALTIME === 'false'
        ? false
        : ENV !== 'development',

  // Retry configuration
  retryAttempts: 3,
  retryDelay: 1000,

  // Headers
  defaultHeaders: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'web',
    'X-Client-Version': import.meta.env.VITE_APP_VERSION || '1.0.0',
  },
};

// API Endpoints grouped by feature
export const ENDPOINTS = {
  // Authentication
  auth: {
    login: '/api/auth/login',
    register: '/api/auth/register',
    logout: '/api/auth/logout',
    refresh: '/api/auth/refresh',
    me: '/api/auth/me',
    verify: '/api/auth/verify-email',
    resetPassword: '/api/auth/reset-password',
  },

  // Dashboard
  dashboard: {
    overview: '/api/dashboard/overview',
    commandCenter: '/api/dashboard/command-center',
    scStats: '/api/dashboard/sc-stats',
    gcStats: '/api/dashboard/gc-stats',
    adminStats: '/api/dashboard/admin-stats',
    industrialStats: '/api/dashboard/industrial-stats',
    metrics: '/api/dashboard/metrics',
    alerts: '/api/dashboard/alerts',
    activities: '/api/dashboard/activities',
  },

  // Analytics
  analytics: {
    dashboard: '/api/analytics/dashboard',
    safety: '/api/analytics/safety',
  },

  // Safety Data
  // NOTE: Legacy /api/safety/* prefix removed 2026-02-10 — those routes are not mounted
  // in vh-api server.js. See docs/source-of-truth/API_CONTRACT_ALIGNMENT.md
  safety: {
    incidents: '/api/incidents',
    events: '/api/safety-events',
    trends: '/api/safety-events/trends',
    hazards: '/api/safety-events', // Use ?eventType=hazard for filtering
    inspections: '/api/safety-events', // Use ?eventType=inspection for filtering
    training: '/api/training/modules',
    reports: '/api/reports/safety-stats',
    // FOREMAN-WAVE-1 item #8 — toolboxTalks reshaped from string to object so
    // service helpers (services/api/toolboxTalks.js) can reach generate +
    // per-talk subroutes. Zero pre-existing consumers grep'd at envelope time.
    toolboxTalks: {
      list: '/api/toolbox-talks',
      generate: '/api/toolbox-talks/generate',
      get: (id) => `/api/toolbox-talks/${id}`,
      scheduled: (id) => `/api/toolbox-talks/${id}/scheduled`,
      deliver: (id) => `/api/toolbox-talks/${id}/deliver`,
      confirm: (id) => `/api/toolbox-talks/${id}/confirm`,
    },
    correctiveActions: {
      list: '/api/corrective-actions',
      get: (id) => `/api/corrective-actions/${id}`,
      status: (id) => `/api/corrective-actions/${id}/status`,
    },
    permits: '/api/permits', // NOTE: permits route is commented out in server.js
  },

  // Ingestion (Mobile → Backend)
  ingest: {
    safety: '/api/ingest/safety',
    photo: '/api/ingest/photo',
    hazard: '/api/ingest/hazard',
    incident: '/api/ingest/incident',
    jsa: '/api/ingest/jsa',
    toolboxTalk: '/api/ingest/toolbox-talk',
  },

  // Projects/Jobsites
  projects: {
    list: '/api/jobsites',
    get: (id) => `/api/jobsites/${id}`,
    create: '/api/jobsites',
    update: (id) => `/api/jobsites/${id}`,
    delete: (id) => `/api/jobsites/${id}`,
    summary: '/api/jobsites/summary',
    safety: (id) => `/api/jobsites/${id}/safety`,
    workers: (id) => `/api/jobsites/${id}/workers`,
    compliance: (id) => `/api/jobsites/${id}/compliance`,
    activity: (id) => `/api/jobsites/${id}/activity`,
    notifications: (id) => `/api/jobsites/${id}/notifications`,
    contractors: (id) => `/api/jobsites/${id}/contractors`,
    documents: (id) => `/api/jobsites/${id}/documents`,
    documentSets: (id) => `/api/jobsites/${id}/document-sets`,
    documentSet: (jobsiteId, setId) => `/api/jobsites/${jobsiteId}/document-sets/${setId}`,
    blueprints: (id) => `/api/jobsites/${id}/blueprints`,
    photos: (id) => `/api/jobsites/${id}/photos`,
    messages: (id) => `/api/jobsites/${id}/messages`,
    crews: (id) => `/api/jobsites/${id}/crews`,
    suggestions: '/api/jobsites/suggestions',
    upcomingItems: (id) => `/api/jobsites/${id}/upcoming-items`,
    acknowledgeAlert: (jobsiteId, alertId) =>
      `/api/jobsites/${jobsiteId}/alerts/${alertId}/acknowledge`,
  },

  // Documents (Bolt jobsite document library — GET /api/documents/:id; metadata via pm classification)
  documents: {
    list: '/api/documents',
    get: (id) => `/api/documents/${id}`,
    classification: (id) => `/api/pm-documents/${id}/classification`,
    stats: '/api/documents/stats',
    search: '/api/documents/search',
  },

  // Workers
  workers: {
    list: '/api/workers',
    get: (id) => `/api/workers/${id}`,
    create: '/api/workers',
    update: (id) => `/api/workers/${id}`,
    summary: '/api/workers/summary',
    me: '/api/workers/me',
    meProfile: '/api/workers/me/profile',
    meCertifications: '/api/workers/me/certifications',
    certifications: (id) => `/api/workers/${id}/certifications`,
    certificationsSummary: '/api/workers/certifications/summary',
    activity: (id) => `/api/workers/${id}/activity`,
    importPreview: '/api/workers/import/preview',
    import: '/api/workers/import',
    assignment: (id) => `/api/workers/${id}/assignment`,
    training: (id) => `/api/workers/${id}/training`,
    incidents: (id) => `/api/workers/${id}/incidents`,
    attendance: (id) => `/api/workers/${id}/attendance`,
  },

  // Organization memberships (worker roster)
  orgMemberships: {
    byOrganization: (orgId) => `/api/org-memberships/organization/${orgId}`,
  },

  // Access management (role catalog, custom roles, per-membership allocation)
  accessManagement: {
    catalog: '/api/access-management/catalog',
    roles: '/api/access-management/roles',
    role: (roleId) => `/api/access-management/roles/${roleId}`,
    membershipRole: (membershipId) => `/api/access-management/memberships/${membershipId}/role`,
    membershipPermissions: (membershipId) => `/api/access-management/memberships/${membershipId}/permissions`,
    effectiveAccess: (membershipId) => `/api/access-management/memberships/${membershipId}/effective-access`,
  },

  // Invites
  invites: {
    organizationList: '/api/organizations/invites',
    workerList: '/api/worker-invites',
    workerCreate: '/api/worker-invites',
    workerStats: '/api/worker-invites/stats',
    summary: '/api/worker-invites/summary',
    validate: (token) => `/api/worker-invites/validate/${token}`,
    myInvites: '/api/worker-invites/my-invites',
    accept: (id) => `/api/worker-invites/${id}/accept-in-app`,
    reject: (id) => `/api/worker-invites/${id}/reject`,
    resend: (id) => `/api/worker-invites/${id}/resend`,
    teamResend: (id) => `/api/team-invites/${id}/resend`,
    revoke: (id) => `/api/worker-invites/${id}/revoke`,
  },

  // Compliance
  compliance: {
    requirements: '/api/compliance/requirements',
    metrics: '/api/compliance/metrics',
    actionQueue: '/api/compliance/action-queue',
    cliffTimeline: '/api/compliance/cliff-timeline',
    status: '/api/compliance-reports/dashboard/summary',
    workforceSummary: '/api/compliance-reports/workforce/summary',
    reports: '/api/compliance/reports',
    export: '/api/compliance-reports/export',
    osha: '/api/compliance/osha',
  },

  // Onboarding
  onboarding: {
    templates: '/api/onboarding/templates',
    start: (workerId) => `/api/onboarding/workers/${workerId}/start`,
    status: (workerId) => `/api/onboarding/workers/${workerId}/status`,
    progress: (workerId) => `/api/onboarding/workers/${workerId}/progress`,
    completeStep: (workerId, stepId) => `/api/onboarding/workers/${workerId}/steps/${stepId}/complete`,
    skipStep: (workerId, stepId) => `/api/onboarding/workers/${workerId}/steps/${stepId}/skip`,
  },

  // Reminders
  reminders: {
    create: '/api/reminders',
  },

  // Notifications
  notifications: {
    settings: '/api/notifications/settings',
    preferences: '/api/notifications/preferences',
    inbox: '/api/hierarchy-messages/inbox',
    unreadCount: '/api/hierarchy-messages/unread-count',
    markRead: (messageId) => `/api/hierarchy-messages/${messageId}/read`,
  },

  /** Worker / hierarchy messaging (same Message collection as notification inbox) */
  hierarchyMessages: {
    inbox: '/api/hierarchy-messages/inbox',
    outbox: '/api/hierarchy-messages/outbox',
    unreadCount: '/api/hierarchy-messages/unread-count',
    thread: (threadId) => `/api/hierarchy-messages/thread/${encodeURIComponent(threadId)}`,
    markRead: (messageId) => `/api/hierarchy-messages/${messageId}/read`,
    send: '/api/hierarchy-messages/send',
    sendToForeman: '/api/hierarchy-messages/send-to-foreman',
    broadcast: '/api/hierarchy-messages/broadcast',
    summon: '/api/hierarchy-messages/summon',
    myForeman: '/api/hierarchy-messages/my-foreman',
    directReports: '/api/hierarchy-messages/direct-reports',
  },

  // AI/ML Services
  ai: {
    quiz: '/api/ai/quiz',
    assistant: '/api/ai-assistant',
    predictions: '/api/ml/predictions',
    riskScore: '/api/ml/score_risk',
    actions: '/api/command-center/actions',
  },

  // Command Center
  commandCenter: {
    actions: '/api/command-center/actions',
    metrics: '/api/shifts/metrics',
    dailyBriefing: '/api/command-center/daily-briefing',
    insights: '/api/ai-assistant/insights',
  },

  // Shifts / jobsite presence
  shifts: {
    active: '/api/shifts/active',
  },

  // Crews (first-class org entity — CREW-W1B)
  crews: {
    list: '/api/crews',
    me: '/api/crews/me',
    detail: (crewId) => `/api/crews/${crewId}`,
    create: '/api/crews',
    update: (crewId) => `/api/crews/${crewId}`,
    transferMember: (crewId) => `/api/crews/${crewId}/transfer-member`,
    activate: (crewId) => `/api/crews/${crewId}/activate`,
    archive: (crewId) => `/api/crews/${crewId}/archive`,
    seedPreview: '/api/crews/seed/preview',
    seedConfirm: '/api/crews/seed/confirm',
  },

  // MCP Services
  mcp: {
    health: '/api/mcp/health',
    rag: '/api/mcp/rag/query',
    ml: '/api/mcp/ml/score_risk',
    system: '/api/mcp/system/health',
  },

  // Reports
  reports: {
    safety: '/api/reports/safety-stats',
    compliance: '/api/reports/compliance',
    hazards: '/api/reports/hazards',
    export: (type) => `/api/reports/export/${type}`,
    templates: '/api/reports/templates',
    definitions: '/api/reports/definitions',
    definition: (id) => `/api/reports/definitions/${id}`,
    runs: '/api/reports/runs',
    run: (runId) => `/api/reports/runs/${runId}`,
    download: (runId) => `/api/reports/runs/${runId}/download`,
  },

  // Agents
  agents: {
    specs: '/api/agents/specs',
    spec: (agentId) => `/api/agents/specs/${agentId}`,
    runs: '/api/agents/runs',
    run: (runId) => `/api/agents/runs/${runId}`,
    proposals: '/api/agents/proposals',
    proposal: (proposalId) => `/api/agents/proposals/${proposalId}`,
    approve: (proposalId) => `/api/agents/proposals/${proposalId}/approve`,
    reject: (proposalId) => `/api/agents/proposals/${proposalId}/reject`,
  },

  // Subscriptions
  subscriptions: {
    status: '/api/subscriptions',
    create: '/api/subscriptions/create',
    cancel: '/api/subscriptions/cancel',
    portal: '/api/subscriptions/portal',
  },

  // Organizations
  organizations: {
    list: '/api/organizations',
    get: (id) => `/api/organizations/${id}`,
    create: '/api/organizations',
    update: (id) => `/api/organizations/${id}`,
  },

  // Organization settings (legacy /api/gc-settings path)
  gcSettings: {
    get: '/api/gc-settings',
    update: '/api/gc-settings',
  },

  // Contractors
  contractors: {
    list: '/api/contractors',
    get: (id) => `/api/contractors/${id}`,
    create: '/api/contractors',
    update: (id) => `/api/contractors/${id}`,
    delete: (id) => `/api/contractors/${id}`,
    compliance: (id) => `/api/contractors/${id}/compliance`,
    recommendations: '/api/contractors/recommendations',
  },

  // Equipment
  equipment: {
    list: '/api/equipment',
    get: (id) => `/api/equipment/${id}`,
    create: '/api/equipment',
    update: (id) => `/api/equipment/${id}`,
    delete: (id) => `/api/equipment/${id}`,
    stats: '/api/equipment/stats',
    dueInspection: '/api/equipment/due-inspection',
    bulkImport: '/api/equipment/bulk-import',
    template: '/api/equipment/template',
  },

  // Worker permit requests (foreman decision queue — PERMIT-A frozen contract)
  permitRequests: {
    pending: '/api/permits/requests/pending',
    approve: (requestId) => `/api/permits/requests/${requestId}/approve`,
    reject: (requestId) => `/api/permits/requests/${requestId}/reject`,
  },

  // Worker Hours
  workerHours: {
    list: '/api/worker-hours',
    get: (id) => `/api/worker-hours/${id}`,
    create: '/api/worker-hours',
    update: (id) => `/api/worker-hours/${id}`,
    delete: (id) => `/api/worker-hours/${id}`,
    summary: '/api/worker-hours/summary',
    trirData: '/api/worker-hours/trir-data',
    bulkImport: '/api/worker-hours/bulk',
    template: '/api/worker-hours/template',
  },

  // Project Management (PM)
  pm: {
    rfis: {
      list: '/api/rfis',
      detail: (id) => `/api/rfis/${id}`,
      create: '/api/rfis',
      update: (id) => `/api/rfis/${id}`,
      delete: (id) => `/api/rfis/${id}`,
      stats: '/api/rfis/stats',
      overdue: '/api/rfis/overdue',
      transition: (id) => `/api/rfis/${id}/transition`,
      responses: (id) => `/api/rfis/${id}/responses`,
    },
    submittals: {
      list: '/api/submittals',
      detail: (id) => `/api/submittals/${id}`,
      create: '/api/submittals',
      update: (id) => `/api/submittals/${id}`,
      delete: (id) => `/api/submittals/${id}`,
      stats: '/api/submittals/stats',
      overdue: '/api/submittals/overdue',
      transition: (id) => `/api/submittals/${id}/transition`,
      revisions: (id) => `/api/submittals/${id}/revisions`,
      approve: (id) => `/api/submittals/${id}/approve`,
    },
    dailyLogs: {
      list: '/api/daily-logs',
      detail: (id) => `/api/daily-logs/${id}`,
      create: '/api/daily-logs',
      update: (id) => `/api/daily-logs/${id}`,
      delete: (id) => `/api/daily-logs/${id}`,
      complete: (id) => `/api/daily-logs/${id}/complete`,
      distribute: (id) => `/api/daily-logs/${id}/distribute`,
      stats: '/api/daily-logs/stats',
    },
    permits: {
      list: '/api/permits',
      detail: (id) => `/api/permits/${id}`,
      create: '/api/permits',
      update: (id) => `/api/permits/${id}`,
      delete: (id) => `/api/permits/${id}`,
      transition: (id) => `/api/permits/${id}/transition`,
      stats: '/api/permits/stats',
    },
    changeOrders: {
      list: '/api/change-orders',
      detail: (id) => `/api/change-orders/${id}`,
      create: '/api/change-orders',
      update: (id) => `/api/change-orders/${id}`,
      delete: (id) => `/api/change-orders/${id}`,
      stats: '/api/change-orders/stats',
      transition: (id) => `/api/change-orders/${id}/transition`,
    },
    punchItems: {
      list: '/api/punch-items',
      detail: (id) => `/api/punch-items/${id}`,
      create: '/api/punch-items',
      update: (id) => `/api/punch-items/${id}`,
      delete: (id) => `/api/punch-items/${id}`,
      stats: '/api/punch-items/stats',
      transition: (id) => `/api/punch-items/${id}/transition`,
      attachments: (id) => `/api/punch-items/${id}/attachments`,
    },
    jobsiteTasks: {
      list: '/api/jobsite-tasks',
      detail: (id) => `/api/jobsite-tasks/${id}`,
      linkedDocuments: (id) => `/api/jobsite-tasks/${id}/linked-documents`,
      suggestedSheets: (id) => `/api/jobsite-tasks/${id}/suggested-sheets`,
    },
    meetings: {
      list: '/api/meetings',
      detail: (id) => `/api/meetings/${id}`,
      create: '/api/meetings',
      update: (id) => `/api/meetings/${id}`,
      delete: (id) => `/api/meetings/${id}`,
      actionItems: (id) => `/api/meetings/${id}/action-items`,
      actionItem: (id, itemId) => `/api/meetings/${id}/action-items/${itemId}`,
      markOccurred: (id) => `/api/meetings/${id}/mark-occurred`,
      stats: '/api/meetings/stats',
    },
    documents: {
      list: '/api/pm-documents',
      revisions: (id) => `/api/pm-documents/${id}/revisions`,
      addRevision: (id) => `/api/pm-documents/${id}/revisions`,
      classification: (id) => `/api/pm-documents/${id}/classification`,
      link: (id) => `/api/pm-documents/${id}/link`,
      delete: (id) => `/api/pm-documents/${id}`,
      stats: '/api/pm-documents/stats',
    },
    projects: {
      list: '/api/projects',
      create: '/api/projects',
      detail: (id) => `/api/projects/${id}`,
      byJobsite: (jobsiteId) => `/api/projects?jobsiteId=${jobsiteId}`,
    },
  },

  planningSessions: {
    base: '/api/planning-sessions',
    get: (id) => `/api/planning-sessions/${id}`,
    inFlight: '/api/planning-sessions/in-flight',
    turns: (id) => `/api/planning-sessions/${id}/turns`,
    answer: (id) => `/api/planning-sessions/${id}/answer`,
    decide: (id) => `/api/planning-sessions/${id}/decide`,
    materialize: (id) => `/api/planning-sessions/${id}/materialize`,
    manualEdits: (id) => `/api/planning-sessions/${id}/manual-edits`,
  },

  // OSHA Compliance
  osha: {
    log300: '/api/osha/300-log',
    log300Entry: (id) => `/api/osha/300-log/${id}`,
    summary300a: (year) => `/api/osha/300a-summary/${year}`,
    import: '/api/osha/300-log/import',
  },

  // Global search (F18B — tenant-scoped fan-out over seven surfaces)
  search: {
    global: '/api/search',
  },

  // Learning platform (org-side). Admin assignments pair with unmerged api lane.
  learning: {
    leaderboard: '/api/learning/leaderboard',
    tracks: '/api/learning/tracks',
    adminAssignments: '/api/learning/admin/assignments',
    adminAssignment: (id) => `/api/learning/admin/assignments/${id}`,
  },
};

// Utility function to construct full URL
export const getFullUrl = (endpoint) => {
  if (endpoint.startsWith('http')) return endpoint;
  if (endpoint.startsWith('/api')) return `${API_BASE_URL}${endpoint}`;
  return `${API_BASE_URL}/api${endpoint}`;
};

// Demo/mock toggles removed. Demo mode is hosted in vh-demo-web.

// Export for debugging
if (isDevelopment()) {
  logger.info('[API Config] Environment:', ENV);
  logger.info('[API Config] Base URL:', API_BASE_URL);
}

export default API_CONFIG;
